import { isPlatformBrowser } from "@angular/common";
import { Component, HostBinding, Inject, OnDestroy, OnInit, PLATFORM_ID } from "@angular/core";
import { MainState } from "@app/store/state";
import { UserSubscriptionInterface } from "@core/interfaces/user-subscription.interface";
import { AuthService } from "@core/services/auth.service";
import { PopNotificationsService } from "@core/services/pop-notifications.service";
import { UserSubscriptionService } from "@core/services/user-subscription/user-subscription.service";
import { selectCurrentUser, selectCurrentUserSubscriptions } from "@features/account/store/auth.selectors";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { combineLatest, Observable, Subject } from "rxjs";
import { map, takeUntil, tap } from "rxjs/operators";

enum NudgeType {
  DISCOVER = "discover",
  UPGRADE = "upgrade",
  RENEW = "renew"
}

interface NudgeConfig {
  type: NudgeType;
  message: string;
  dismissible: boolean;
  dismissalDays: number;
}

@Component({
  selector: "astrobin-upgrade-nudge",
  templateUrl: "./upgrade-nudge.component.html",
  styleUrls: ["./upgrade-nudge.component.scss"]
})
export class UpgradeNudgeComponent implements OnInit, OnDestroy {
  nudgeConfig$: Observable<NudgeConfig | null>;
  visible = true;

  @HostBinding("class.nudge-visible")
  nudgeVisible = false;

  private readonly _destroyed$ = new Subject<void>();
  private readonly COOKIE_PREFIX = "upgrade-nudge-dismissed";
  private readonly DAYS_IN_MS = 24 * 60 * 60 * 1000;

  constructor(
    private readonly store$: Store<MainState>,
    private readonly authService: AuthService,
    private readonly userSubscriptionService: UserSubscriptionService,
    private readonly translateService: TranslateService,
    private readonly popNotificationsService: PopNotificationsService,
    @Inject(PLATFORM_ID) private readonly platformId: Object
  ) {}

  ngOnInit(): void {
    this.nudgeConfig$ = combineLatest([
      this.authService.isAuthenticated$(),
      this.store$.select(selectCurrentUser),
      this.store$.select(selectCurrentUserSubscriptions),
      combineLatest([
        this.userSubscriptionService.isLite$(),
        this.userSubscriptionService.isPremium$(),
        this.userSubscriptionService.isUltimate$()
      ]).pipe(map(([isLite, isPremium, isUltimate]) => isLite || isPremium || isUltimate))
    ]).pipe(
      takeUntil(this._destroyed$),
      map(([isAuthenticated, user, userSubscriptions, hasValidSubscription]) => {
        if (!isAuthenticated) {
          return {
            type: NudgeType.DISCOVER,
            message: this.translateService.instant(
              "New to AstroBin? Discover how we help astrophotographers share and preserve their work."
            ),
            dismissible: true,
            dismissalDays: 7
          };
        }

        if (!user) {
          return null;
        }

        // Check for expired subscriptions
        const expiredSubscription = this._getMostRecentExpiredSubscription(userSubscriptions);
        if (expiredSubscription && !hasValidSubscription) {
          const daysSinceExpiration = this._getDaysSince(new Date(expiredSubscription.expires));

          return {
            type: NudgeType.RENEW,
            message: this.translateService.instant(
              "Your subscription has expired. Renew to regain access to advanced features"
            ),
            dismissible: true,
            dismissalDays: daysSinceExpiration < 30 ? 7 : 30
          };
        }

        // Show upgrade nudge for free tier users
        if (!hasValidSubscription) {
          const daysSinceJoined = this._getDaysSince(new Date(user.dateJoined));

          return {
            type: NudgeType.UPGRADE,
            message: this.translateService.instant(
              "Upgrade to enjoy additional features and support AstroBin's mission"
            ),
            dismissible: true,
            dismissalDays: daysSinceJoined < 30 ? 7 : 30
          };
        }

        // User has a valid subscription, no nudge needed
        return null;
      }),
      map(config => {
        if (config && config.dismissible && this._isDismissed(config.type, config.dismissalDays)) {
          return null;
        }
        return config;
      }),
      tap(config => {
        this.nudgeVisible = !!config;
      })
    );
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  dismiss(config: NudgeConfig): void {
    if (!config.dismissible || !isPlatformBrowser(this.platformId)) {
      return;
    }

    const cookieName = `${this.COOKIE_PREFIX}-${config.type}`;
    const expirationDays = config.dismissalDays;
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + expirationDays);

    // Set cookie using document.cookie directly
    document.cookie = `${cookieName}=${Date.now()};expires=${expirationDate.toUTCString()};path=/`;
    this.visible = false;
    this.nudgeVisible = false;

    // Show notification about when the reminder will appear again
    const title = this.translateService.instant("Got it!");
    const message = this.translateService.instant("We'll remind you in {{0}} days", { 0: expirationDays });
    this.popNotificationsService.info(message, title, { timeOut: 4000 });
  }

  private _isDismissed(type: NudgeType, dismissalDays: number): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    const cookieName = `${this.COOKIE_PREFIX}-${type}`;
    const cookies = document.cookie.split(";");

    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split("=");
      if (name === cookieName && value) {
        const dismissedDate = new Date(parseInt(value, 10));
        const daysSinceDismissed = this._getDaysSince(dismissedDate);
        return daysSinceDismissed < dismissalDays;
      }
    }

    return false;
  }

  private _getDaysSince(date: Date): number {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    return Math.floor(diffMs / this.DAYS_IN_MS);
  }

  private _getMostRecentExpiredSubscription(
    subscriptions: UserSubscriptionInterface[]
  ): UserSubscriptionInterface | null {
    if (!subscriptions || subscriptions.length === 0) {
      return null;
    }

    const now = new Date();
    const expiredSubs = subscriptions.filter(sub => {
      if (!sub || !sub.expires) {
        return false;
      }
      const expires = new Date(sub.expires);
      return expires <= now;
    });

    if (expiredSubs.length === 0) {
      return null;
    }

    // Sort by expiration date, most recent first
    return expiredSubs.sort((a, b) => {
      const dateA = new Date(a.expires).getTime();
      const dateB = new Date(b.expires).getTime();
      return dateB - dateA;
    })[0];
  }
}
